/*
 * Copyright (c) 2023 - 2026. Bolmate B.V.
 */
import './style/creditInvoiceData.scss';

import {
    ChangeKeyFunction,
    ElementPosition,
    ErrorType,
    Loader,
    NumberString,
    parseMoney,
    voidFunction
} from '@dvrd/dvr-controls';

import React, {FocusEventHandler, Fragment, MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import Invoice from '../../../models/invoiceModel';

import {BolmateButton, BolmateListCard, BolmateNumberInput} from '../../controls';

import InvoiceData from '../data/invoiceData';

import {CreditItem} from '../../../models/orderItemModel';

import classNames from 'classnames';

import {EditMode} from '../../../util/interfaces';

import {translateVatValue} from '../../../util/vatUtil';

interface Props {
    onChangeField: ChangeKeyFunction;
    onChangePriceIncl: ChangeKeyFunction;
    onChangePercentage: ChangeKeyFunction;
    onChangeQuantity: ChangeKeyFunction;
    onSubmit: MouseEventHandler;
    onClose: MouseEventHandler;
    onResetError: (id: string) => FocusEventHandler;
    items: CreditItem[];
    invoice: Invoice;
    loading: boolean;
    errors: Record<string, ErrorType>;
    mode: EditMode;
}

export default function CreditInvoiceData(props: Props) {
    const {t} = useTranslation();
    const {
        invoice,
        loading,
        onSubmit,
        items,
        onChangePercentage,
        onChangePriceIncl,
        onChangeQuantity,
        onClose,
        onResetError,
        errors,
        mode,
        onChangeField
    } = props;

    function renderItems() {
        return (
            <BolmateListCard
                className='order-items-card'
                title={t('invoice.credit.itemsHeader', 'Te Crediteren Productregels')}
                count={items.length}
            >
                <div className='order-items'>
                    <Loader active={loading} />
                    <div className='row head'>
                        <label>{t('invoice.credit.col.ean', 'EAN')}</label>
                        <label>{t('invoice.credit.col.name', 'NAAM')}</label>
                        <label>{t('invoice.credit.col.priceIncl', 'PRIJS INCL. BTW')}</label>
                        <label>{t('invoice.credit.col.quantity', 'AANTAL')}</label>
                        <label>{t('invoice.credit.col.vat', 'BTW')}</label>
                        <label>{t('invoice.credit.col.subtotalIncl', 'SUBTOTAAL INCL. BTW')}</label>
                    </div>
                    {items.map(renderItem)}
                </div>
            </BolmateListCard>
        );
    }

    function renderItem(item: CreditItem) {
        const {orderItem} = item;
        if (!orderItem) return null;
        const error = errors[orderItem.id];
        return (
            <Fragment key={orderItem.id}>
                <div className='row order'>
                    <label className='item-ean'>{orderItem.ean}</label>
                    <label className='item-title'>{orderItem.title}</label>
                    <label className='item-piece-price'>&euro; {parseMoney(orderItem.piecePrice)}</label>
                    <label className='item-quantity'>{orderItem.quantity}</label>
                    <label className='vat-label item-vat'>{translateVatValue(orderItem.vat)}</label>
                    <label className='item-subtotal'>&euro; {parseMoney(orderItem.price)}</label>
                </div>
                <div className='row'>
                    <label />
                    <label className={classNames('error', error && 'visible')}>{error}</label>
                    <div className='price-container'>
                        <BolmateNumberInput
                            // The controller contract is NumberString-based
                            // (it reads .numberValue) — wrap the raw input
                            // string before handing it over.
                            onChange={(value: string | number) =>
                                onChangePriceIncl(orderItem.id)(new NumberString(value))
                            }
                            value={item.priceIncl.stringValue}
                            label={t('invoice.credit.price', 'Prijs')}
                            onFocus={onResetError(orderItem.id)}
                            shape='rounded'
                            uniformFont
                            ornaments={{
                                element: <span>- &euro;</span>,
                                placement: ElementPosition.LEFT
                            }}
                            asCurrency
                        />
                        <BolmateNumberInput
                            onChange={(value: string | number) =>
                                onChangePercentage(orderItem.id)(new NumberString(value))
                            }
                            value={item.percentage?.stringValue ?? ''}
                            label={t('invoice.credit.percentage', 'Percentage')}
                            onFocus={onResetError(orderItem.id)}
                            shape='rounded'
                            uniformFont
                            ornaments={[
                                {
                                    element: <span>-</span>,
                                    placement: ElementPosition.LEFT
                                },
                                {element: <span>%</span>, placement: ElementPosition.RIGHT}
                            ]}
                        />
                    </div>
                    <BolmateNumberInput
                        onChange={(value: string | number) => onChangeQuantity(orderItem.id)(new NumberString(value))}
                        value={item.quantity.stringValue}
                        label={t('invoice.credit.quantity', 'Aantal')}
                        onFocus={onResetError(orderItem.id)}
                        shape='rounded'
                        uniformFont
                    />
                    <BolmateNumberInput
                        onChange={voidFunction}
                        value={item.vatAmount}
                        label={t('invoice.credit.vat', 'BTW')}
                        disabled
                        shape='rounded'
                        uniformFont
                        ornaments={{
                            element: <span>- &euro;</span>,
                            placement: ElementPosition.LEFT
                        }}
                        asCurrency
                    />
                    <label className='item-subtotal'>&euro; {parseMoney(item.subTotalIncl)}</label>
                </div>
            </Fragment>
        );
    }

    const saveText =
        mode === EditMode.EDIT ? t('invoice.edit.save', 'Opslaan') : t('invoice.credit.create', 'Aanmaken');
    return (
        <div className='credit-invoice-data'>
            <div className='header-container'>
                <BolmateButton onClick={onClose} label={t('common.cancel', 'Annuleren')} disabled={loading} tertiary />
                <BolmateButton
                    onClick={onSubmit}
                    label={`${saveText} ${t('invoice.credit.andToCreditInvoice', 'en naar creditfactuur')}`}
                    disabled={loading}
                />
            </div>
            <InvoiceData invoice={invoice} onChange={onChangeField} disabled />
            {renderItems()}
        </div>
    );
}
